import { useState, useEffect, useCallback } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import axiosInstance, { getUserDetails, hasScientistPondUIAccess } from '../../components/content/axiosConfig';
import Preloader from '../../components/custom/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/toast/toast';
import { toDateInputValue } from '../../utils/toDateInputValue';

const FEEDER_TYPE_OPTIONS = [
  { value: '', label: 'Select type' },
  { value: 'Demand', label: 'Demand' },
  { value: 'Automatic', label: 'Automatic' },
  { value: 'Manual', label: 'Manual' },
  { value: 'Other', label: 'Other' },
];

/** Map legacy DB values to current dropdown values. */
function normalizeFeederTypeFromApi(type) {
  if (type == null || type === '') return '';
  const t = String(type).trim();
  if (t === 'Hand-fed') return 'Manual';
  if (t === 'Solar') return '';
  return t;
}

const emptyFeederRow = () => ({
  FeederName: '',
  FeederType: '',
  FeedTypeBrand: '',
  FeedFrequency: '',
  FeedRate: '',
  FeedDate: '',
});

const emptyFertilizationRow = () => ({
  FertilizationDate: '',
  FertilizerType: '',
  AmountPoundsPerAcre: '',
});

const inactiveTabStyle = {
  color: '#6c757d',
  backgroundColor: 'transparent',
  border: 'none',
  fontWeight: '500',
  fontSize: '0.9rem',
};

const activeTabStyle = {
  color: '#348fe2',
  backgroundColor: '#ffffff',
  border: '2px solid #348fe2',
  borderBottom: 'none',
  fontWeight: '500',
  fontSize: '0.9rem',
};

const PondFeedFertilization = () => {
  const history = useHistory();
  const { id: pondID } = useParams();
  const userDetails = getUserDetails();
  const [pondFisherID, setPondFisherID] = useState(null);
  const fisherID = userDetails.FisherID ?? pondFisherID;
  const canEditFeedFert = hasScientistPondUIAccess(userDetails);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pondName, setPondName] = useState('');
  const [feederRows, setFeederRows] = useState([emptyFeederRow()]);
  const [fertilizationRows, setFertilizationRows] = useState([emptyFertilizationRow()]);

  const handleApiError = useCallback((error, defaultMessage) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.reload();
      showErrorToast('Session expired. Please log in again.');
    } else {
      const errorMessage = error.response?.data?.message || defaultMessage;
      showErrorToast(errorMessage);
      console.error(defaultMessage, error);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pondResponse = await axiosInstance.get(`/api/Pond/GetPondByID`, {
          params: { id: pondID },
        });
        if (cancelled) return;
        const pond = pondResponse.data;
        setPondName(pond.Name || '');
        setPondFisherID(pond.FisherID ?? null);

        const feedersFromApi = (pond.pondFeeders || []).map((f) => ({
          FeederName: f.FeederName != null && f.FeederName !== '' ? String(f.FeederName) : '',
          FeederType: normalizeFeederTypeFromApi(f.FeederType),
          FeedTypeBrand: f.FeedTypeBrand || '',
          FeedFrequency:
            f.FeedFrequency != null && f.FeedFrequency !== '' ? String(f.FeedFrequency) : '',
          FeedRate: f.FeedRate != null && f.FeedRate !== '' ? String(f.FeedRate) : '',
          FeedDate: f.FeedDate ? toDateInputValue(f.FeedDate) || '' : '',
        }));
        setFeederRows(feedersFromApi.length ? feedersFromApi : [emptyFeederRow()]);

        const fertsFromApi = (pond.pondFertilizations || []).map((r) => ({
          FertilizationDate: r.FertilizationDate ? toDateInputValue(r.FertilizationDate) || '' : '',
          FertilizerType: r.FertilizerType || '',
          AmountPoundsPerAcre:
            r.AmountPoundsPerAcre != null && r.AmountPoundsPerAcre !== ''
              ? String(r.AmountPoundsPerAcre)
              : '',
        }));
        setFertilizationRows(fertsFromApi.length ? fertsFromApi : [emptyFertilizationRow()]);
      } catch (error) {
        if (!cancelled) handleApiError(error, 'Failed to load pond data');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pondID, handleApiError]);

  const saveFeedAndFertilization = async (exitAfterSave) => {
    if (!canEditFeedFert) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/Pond/UpdatePond/${pondID}`, {
        FisherID: fisherID,
        pondFeeders: feederRows.map((r) => ({
          FeederName: r.FeederName,
          FeederType: r.FeederType,
          FeedTypeBrand: r.FeedTypeBrand,
          FeedFrequency: r.FeedFrequency,
          FeedRate: r.FeedRate,
          FeedDate: r.FeedDate ? toDateInputValue(r.FeedDate) || null : null,
        })),
        pondFertilizations: fertilizationRows.map((r) => ({
          FertilizationDate: r.FertilizationDate ? toDateInputValue(r.FertilizationDate) || null : null,
          FertilizerType: r.FertilizerType,
          AmountPoundsPerAcre: r.AmountPoundsPerAcre,
        })),
      });
      showSuccessToast(exitAfterSave ? 'Saved — returning to pond list' : 'Feed and fertilization saved');
      if (exitAfterSave) {
        history.push('/pond');
      }
    } catch (error) {
      handleApiError(error, 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const sub = e.nativeEvent.submitter;
    const action = sub?.getAttribute?.('value') ?? sub?.value ?? 'stay';
    saveFeedAndFertilization(action === 'exit');
  };

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page donezo-workflow-page py-3">
      <form onSubmit={handleSubmit}>
        <div className="container-fluid px-3 px-md-4">
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
            <div className="mb-3 mb-md-0">
              <ul className="breadcrumb mb-2" style={{ fontSize: '0.875rem' }}>
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: '#6c757d', textDecoration: 'none' }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/pond" style={{ color: '#6c757d', textDecoration: 'none' }}>
                    Pond
                  </Link>
                </li>
                <li className="breadcrumb-item active" style={{ color: '#495057' }}>
                  Feed &amp; Fertilization
                </li>
              </ul>
              <h1
                className="mb-0 page-header d-flex align-items-center gap-3 flex-wrap"
                style={{ fontSize: '1.75rem', fontWeight: '600', color: '#2d353c' }}
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-3 text-white shadow-sm flex-shrink-0"
                  style={{
                    width: 52,
                    height: 52,
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  }}
                  aria-hidden
                >
                  <i className="fas fa-leaf" style={{ fontSize: '1.35rem' }} />
                </span>
                <span>
                  Feed &amp; Fertilization
                  {pondName ? (
                    <span className="text-muted fw-normal fs-5 ms-2">— {pondName}</span>
                  ) : null}
                </span>
              </h1>
            </div>
            {canEditFeedFert && (
              <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                <button
                  type="submit"
                  name="saveAction"
                  value="stay"
                  className="btn btn-outline-success px-3 px-md-4 py-2 rounded-pill shadow-sm"
                  style={{ fontSize: '0.9rem', fontWeight: '500' }}
                  disabled={saving}
                >
                  <i className="fa fa-save me-2" />
                  Save
                </button>
                <button
                  type="submit"
                  name="saveAction"
                  value="exit"
                  className="btn btn-success px-3 px-md-4 py-2 rounded-pill shadow-sm"
                  style={{ fontSize: '0.9rem', fontWeight: '500' }}
                  disabled={saving}
                >
                  <i className="fa fa-check me-2" />
                  Save and exit
                </button>
              </div>
            )}
          </div>

          <div className="card border-0 shadow-sm mb-4 donezo-workflow-tabs">
            <ul className="nav nav-tabs border-0 px-3 pt-3" style={{ borderBottom: '2px solid #e9ecef' }}>
              <li className="nav-item me-2">
                <Link
                  to={`/updatePond/${pondID}`}
                  className="nav-link px-3 py-2 rounded-top"
                  style={inactiveTabStyle}
                >
                  <i className="fas fa-cog me-2" aria-hidden />
                  General
                </Link>
              </li>
              <li className="nav-item me-2">
                <Link
                  to={`/pondFeedFertilization/${pondID}`}
                  className="nav-link px-3 py-2 rounded-top"
                  style={activeTabStyle}
                >
                  <i className="fas fa-leaf me-2" aria-hidden />
                  Feed &amp; Fertilization
                </Link>
              </li>
              <li className="nav-item me-2">
                <Link
                  to={`/pondStocking/${pondID}`}
                  className="nav-link px-3 py-2 rounded-top"
                  style={inactiveTabStyle}
                >
                  <i className="fas fa-water me-2" aria-hidden />
                  Stocking Data
                </Link>
              </li>
              <li className="nav-item me-2">
                <Link
                  to={`/pondDetailViaPond/${pondID}`}
                  className="nav-link px-3 py-2 rounded-top"
                  style={inactiveTabStyle}
                >
                  <i className="fas fa-chart-line me-2" aria-hidden />
                  Fishing / Sampling Data
                </Link>
              </li>
            </ul>
          </div>

          <style>{`
            .feed-fertil-stock-table { table-layout: fixed; width: 100%; }
            .feed-fertil-stock-table th,
            .feed-fertil-stock-table td { vertical-align: middle; }
            .feed-fertil-stock-table .form-control,
            .feed-fertil-stock-table .form-select { max-width: 100%; }
            .feed-fertil-table-shell {
              border-radius: 12px;
              overflow: hidden;
              border: 1px solid #e2e8f0;
              background: #fff;
              box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
            }
            .feed-fertil-stock-table thead th {
              font-size: 0.72rem;
              text-transform: uppercase;
              letter-spacing: 0.045em;
              color: #475569;
              font-weight: 600;
              border-bottom: 2px solid #e2e8f0 !important;
              background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%) !important;
              white-space: nowrap;
            }
            .feed-fertil-stock-table tbody tr:hover { background-color: rgba(52, 143, 226, 0.045); }
          `}</style>

          {!canEditFeedFert && (
            <p className="text-muted small mb-3">Only administrators can edit feed and fertilization.</p>
          )}

          <div className="row g-3 g-md-4">
            <div className="col-12">
              <div
                className="card border-0 shadow-sm mb-0"
                style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}
              >
                <div
                  className="card-header bg-white border-bottom py-3 px-3 px-md-4 d-flex flex-wrap align-items-center justify-content-between gap-2"
                  style={{ borderRadius: '12px 12px 0 0' }}
                >
                  <h6
                    className="mb-0 fw-bold d-flex align-items-center gap-2"
                    style={{ color: '#2d353c', fontSize: '1rem' }}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                      style={{ width: 36, height: 36, background: 'rgba(111, 66, 193, 0.12)' }}
                      aria-hidden
                    >
                      <i className="fas fa-leaf text-primary" style={{ fontSize: '1rem' }} />
                    </span>
                    Feed
                  </h6>
                </div>
                <div className="card-body p-2 p-md-3 pt-md-2">
                  <div className="table-responsive mb-0 feed-fertil-table-shell">
                    <table className="table table-sm mb-0 feed-fertil-stock-table align-middle">
                      <colgroup>
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '8%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>
                            <i className="fas fa-calendar-day me-1 text-primary opacity-75" aria-hidden />
                            Date
                          </th>
                          <th>
                            <i className="fas fa-signature me-1 text-primary opacity-75" aria-hidden />
                            Feeder name
                          </th>
                          <th>
                            <i className="fas fa-cog me-1 text-primary opacity-75" aria-hidden />
                            Feeder type
                          </th>
                          <th>
                            <i className="fas fa-tag me-1 text-primary opacity-75" aria-hidden />
                            Feed type / brand
                          </th>
                          <th>
                            <i className="fas fa-redo me-1 text-primary opacity-75" aria-hidden />
                            Freq. / day
                          </th>
                          <th>
                            <i className="fas fa-weight-hanging me-1 text-primary opacity-75" aria-hidden />
                            lb / feeding
                          </th>
                          <th className="text-center" style={{ width: '76px' }}>
                            <i className="fas fa-bars me-1 text-secondary" aria-hidden title="Row actions" />
                            <span className="visually-hidden">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {feederRows.map((row, idx) => (
                          <tr key={`feeder-${idx}`}>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="date"
                                className="form-control form-control-sm w-100"
                                value={row.FeedDate}
                                disabled={!canEditFeedFert}
                                onChange={(e) => {
                                  const n = [...feederRows];
                                  n[idx] = { ...n[idx], FeedDate: e.target.value };
                                  setFeederRows(n);
                                }}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="text"
                                className="form-control form-control-sm w-100"
                                placeholder="e.g. North bank"
                                value={row.FeederName}
                                disabled={!canEditFeedFert}
                                onChange={(e) => {
                                  const n = [...feederRows];
                                  n[idx] = { ...n[idx], FeederName: e.target.value };
                                  setFeederRows(n);
                                }}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <select
                                className="form-select form-select-sm w-100"
                                value={row.FeederType}
                                disabled={!canEditFeedFert}
                                onChange={(e) => {
                                  const n = [...feederRows];
                                  n[idx] = { ...n[idx], FeederType: e.target.value };
                                  setFeederRows(n);
                                }}
                              >
                                {FEEDER_TYPE_OPTIONS.map((o) => (
                                  <option key={o.value === '' ? '_empty' : o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="text"
                                className="form-control form-control-sm w-100"
                                value={row.FeedTypeBrand}
                                disabled={!canEditFeedFert}
                                onChange={(e) => {
                                  const n = [...feederRows];
                                  n[idx] = { ...n[idx], FeedTypeBrand: e.target.value };
                                  setFeederRows(n);
                                }}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                className="form-control form-control-sm w-100"
                                value={row.FeedFrequency}
                                disabled={!canEditFeedFert}
                                onChange={(e) => {
                                  const n = [...feederRows];
                                  n[idx] = { ...n[idx], FeedFrequency: e.target.value };
                                  setFeederRows(n);
                                }}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                className="form-control form-control-sm w-100"
                                value={row.FeedRate}
                                disabled={!canEditFeedFert}
                                onChange={(e) => {
                                  const n = [...feederRows];
                                  n[idx] = { ...n[idx], FeedRate: e.target.value };
                                  setFeederRows(n);
                                }}
                              />
                            </td>
                            <td className="text-center align-middle" style={{ minWidth: '76px' }}>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 border-2"
                                style={{ width: '36px', height: '36px' }}
                                disabled={!canEditFeedFert || feederRows.length <= 1}
                                title="Remove feeder"
                                aria-label="Remove feeder row"
                                onClick={() =>
                                  setFeederRows((rows) =>
                                    rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx)
                                  )
                                }
                              >
                                <i className="fas fa-times-circle" style={{ fontSize: '1.1rem', lineHeight: 1 }} aria-hidden />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success rounded-pill px-3 mt-2"
                    disabled={!canEditFeedFert}
                    onClick={() => setFeederRows((r) => [...r, emptyFeederRow()])}
                  >
                    <i className="fas fa-plus-circle me-1" aria-hidden />
                    Add feeder
                  </button>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div
                className="card border-0 shadow-sm mb-0"
                style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}
              >
                <div
                  className="card-header bg-white border-bottom py-3 px-3 px-md-4 d-flex flex-wrap align-items-center justify-content-between gap-2"
                  style={{ borderRadius: '12px 12px 0 0' }}
                >
                  <h6
                    className="mb-0 fw-bold d-flex align-items-center gap-2"
                    style={{ color: '#2d353c', fontSize: '1rem' }}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                      style={{ width: 36, height: 36, background: 'rgba(32, 201, 151, 0.15)' }}
                      aria-hidden
                    >
                      <i className="fas fa-flask text-success" style={{ fontSize: '1rem' }} />
                    </span>
                    Fertilization
                  </h6>
                </div>
                <div className="card-body p-2 p-md-3 pt-md-2">
                  <div className="table-responsive mb-0 feed-fertil-table-shell">
                    <table className="table table-sm mb-0 feed-fertil-stock-table align-middle">
                      <colgroup>
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '28%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '12%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>
                            <i className="fas fa-calendar-day me-1 text-success opacity-75" aria-hidden />
                            Date
                          </th>
                          <th>
                            <i className="fas fa-seedling me-1 text-success opacity-75" aria-hidden />
                            Fertilizer type
                          </th>
                          <th>
                            <i className="fas fa-weight-hanging me-1 text-success opacity-75" aria-hidden />
                            lb / acre
                          </th>
                          <th className="text-center" style={{ width: '76px' }}>
                            <i className="fas fa-bars me-1 text-secondary" aria-hidden title="Row actions" />
                            <span className="visually-hidden">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fertilizationRows.map((row, idx) => (
                          <tr key={`fert-${idx}`}>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="date"
                                className="form-control form-control-sm w-100"
                                value={row.FertilizationDate}
                                disabled={!canEditFeedFert}
                                onChange={(e) => {
                                  const n = [...fertilizationRows];
                                  n[idx] = { ...n[idx], FertilizationDate: e.target.value };
                                  setFertilizationRows(n);
                                }}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="text"
                                className="form-control form-control-sm w-100"
                                value={row.FertilizerType}
                                disabled={!canEditFeedFert}
                                onChange={(e) => {
                                  const n = [...fertilizationRows];
                                  n[idx] = { ...n[idx], FertilizerType: e.target.value };
                                  setFertilizationRows(n);
                                }}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                className="form-control form-control-sm w-100"
                                value={row.AmountPoundsPerAcre}
                                disabled={!canEditFeedFert}
                                onChange={(e) => {
                                  const n = [...fertilizationRows];
                                  n[idx] = { ...n[idx], AmountPoundsPerAcre: e.target.value };
                                  setFertilizationRows(n);
                                }}
                              />
                            </td>
                            <td className="text-center align-middle" style={{ minWidth: '76px' }}>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 border-2"
                                style={{ width: '36px', height: '36px' }}
                                disabled={!canEditFeedFert || fertilizationRows.length <= 1}
                                title="Remove row"
                                aria-label="Remove fertilization row"
                                onClick={() =>
                                  setFertilizationRows((rows) =>
                                    rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx)
                                  )
                                }
                              >
                                <i className="fas fa-times-circle" style={{ fontSize: '1.1rem', lineHeight: 1 }} aria-hidden />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success rounded-pill px-3 mt-2"
                    disabled={!canEditFeedFert}
                    onClick={() => setFertilizationRows((r) => [...r, emptyFertilizationRow()])}
                  >
                    <i className="fas fa-plus-circle me-1" aria-hidden />
                    Add fertilization date
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PondFeedFertilization;
